import { Box, Button, MenuItem, Stack, Typography } from '@mui/material';
import { ChangeEvent, useEffect, useState } from 'react';

import { BankAdditionalRoles, StatusCode } from '../../../api/enumerations';
import { editUser } from '../../../api/users';
import { UserData } from '../../../api/users/types';
import { IOSSwitch, OutlinedButton } from '../../../components/UI/Button';
import {
  StyledTextField,
  menuItemDefaultStyle,
} from '../../../components/UI/Typography';
import {
  IconAddMS,
  IconCloseMS,
  IconEditMS,
  IconRemoveMS,
} from '../../../constants/icons';
import { selectBankProfileUser } from '../../../constants/selectOptions';
import { validateEmail, validateUserName } from '../../../helpers';
import useErrorMessage from '../../../hooks/useErrorMessage';
import useSnackbar from '../../../hooks/useSnackbar';
import { Constants, exclusiveRoles } from '../constants';
import {
  ChipOption,
  CloseButton,
  DialogContainer,
  FormStyled,
  InputWrapper,
  MultiSelectContainer,
  MultiSelectLabel,
  OptionsBox,
  SelectedChipsBox,
  StyledDialog,
  StyledTitle,
  TopBox,
} from './styles';

export interface EditUserDialogProps {
  open: boolean;
  handleClose: () => void;
  userData: UserData;
  updateUsers: () => void;
}

export default function EditUserDialog({
  open,
  handleClose,
  userData,
  updateUsers,
}: EditUserDialogProps): JSX.Element {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [active, setActive] = useState(false);
  const [selectedRole, setSelectedRole] = useState(0);
  const [additionalRole, setAdditionalRole] = useState<BankAdditionalRoles[]>(
    []
  );
  const [emailError, setEmailError] = useState(false);
  const [nameError, setNameError] = useState(false);

  const { handleSnackbar } = useSnackbar();
  const { getErrorMessage } = useErrorMessage();

  useEffect(() => {
    setEmail(userData.email);
    setName(userData.name);
    setSelectedRole(userData.role);
    setActive(userData.is_active);
    if (!userData.additional_roles || userData.additional_roles.length === 0) {
      setAdditionalRole(userData.role ? [userData.role] : []);
    } else {
      setAdditionalRole(userData.additional_roles);
    }
  }, [userData]);

  const resetInputs = (): void => {
    setEmail('');
    setName('');
    setSelectedRole(0);
    setAdditionalRole([]);
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    if (selectedRole === 0) {
      handleSnackbar('Selecione uma função.', true);
      return;
    }
    if (additionalRole.length < 1) {
      handleSnackbar('Selecione o acesso.', true);
      return;
    }

    const updatedData = {
      email,
      role: selectedRole,
      name,
      additionalRoles: additionalRole,
      is_active: active,
    };

    try {
      const response = await editUser(userData.id, updatedData);

      if (response.detail.description)
        throw new Error(response.detail.description);

      if (response.detail.status_code !== StatusCode.OK)
        throw new Error('Algo deu errado, tente novamente.');

      handleSnackbar('Usuário alterado com sucesso!', false);
      updateUsers();
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      resetInputs();
      handleClose();
    }
  };

  const handleChangeRole = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const newRole = Number(e.target.value);
    setSelectedRole(newRole);
    if (newRole !== 0) setAdditionalRole([newRole]);
  };

  const handleAddRole = (roleValue: BankAdditionalRoles): void => {
    if (!additionalRole.includes(roleValue)) {
      setAdditionalRole([...additionalRole, roleValue]);
    }
  };

  const handleRemoveRole = (roleValue: BankAdditionalRoles): void => {
    setAdditionalRole(additionalRole.filter((r) => r !== roleValue));
  };

  const availableOptions = selectBankProfileUser().filter(
    (role) => role.value !== BankAdditionalRoles.SELECT
  );

  const isExclusiveRole = exclusiveRoles.includes(selectedRole);

  return (
    <StyledDialog open={open} onClose={handleClose} aria-labelledby="edit user">
      <DialogContainer>
        <TopBox>
          <CloseButton onClick={handleClose}>{IconCloseMS}</CloseButton>
        </TopBox>
        <StyledTitle>
          {IconEditMS}
          {Constants.editUser}
        </StyledTitle>
        <FormStyled component="form" id="edituser" onSubmit={handleSubmit}>
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography fontWeight={700} color="#707070">
              {Constants.active}
            </Typography>
            <IOSSwitch
              checked={active}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setActive(e.target.checked);
              }}
            />
          </Stack>
          <StyledTextField
            required
            id="name"
            value={name}
            error={nameError}
            label={Constants.userName}
            placeholder={Constants.namePlaceholder}
            onChange={(e) => {
              setName(e.target.value);
              !validateUserName(e.target.value)
                ? setNameError(true)
                : setNameError(false);
            }}
          />
          <StyledTextField
            id="email"
            type="email"
            value={email}
            error={emailError}
            label={Constants.email}
            placeholder={Constants.emailPlaceholder}
            onChange={(e) => {
              setEmail(e.target.value);
              !validateEmail(e.target.value)
                ? setEmailError(true)
                : setEmailError(false);
            }}
          />
          <StyledTextField
            id="role"
            select
            label={Constants.role}
            required
            value={selectedRole}
            onChange={(e) => handleChangeRole(e)}
          >
            {selectBankProfileUser().map((role) => (
              <MenuItem
                disabled={role.value === BankAdditionalRoles.SELECT}
                key={role.value}
                value={role.value}
                sx={{ ...menuItemDefaultStyle }}
              >
                {role.label}
              </MenuItem>
            ))}
          </StyledTextField>
          {selectedRole !== 0 && (
            <MultiSelectContainer>
              <InputWrapper>
                <MultiSelectLabel>{Constants.access} *</MultiSelectLabel>
                <SelectedChipsBox>
                  {additionalRole.map((roleValue) => {
                    const roleOption = availableOptions.find(
                      (opt) => opt.value === roleValue
                    );
                    return (
                      <ChipOption
                        key={roleValue}
                        isSelected
                        onClick={() =>
                          roleValue !== selectedRole &&
                          handleRemoveRole(roleValue)
                        }
                        style={{
                          cursor:
                            roleValue !== selectedRole ? 'pointer' : 'default',
                        }}
                      >
                        {roleOption?.label}
                        {roleValue !== selectedRole && IconRemoveMS}
                      </ChipOption>
                    );
                  })}
                </SelectedChipsBox>
              </InputWrapper>
              {!isExclusiveRole && (
                <OptionsBox>
                  {availableOptions.map((role) => {
                    const isAlreadySelected = additionalRole.includes(
                      role.value as BankAdditionalRoles
                    );
                    return (
                      <ChipOption
                        key={role.value}
                        onClick={() =>
                          !isAlreadySelected &&
                          handleAddRole(role.value as BankAdditionalRoles)
                        }
                        style={{
                          opacity: isAlreadySelected ? 0.5 : 1,
                          cursor: isAlreadySelected ? 'default' : 'pointer',
                        }}
                      >
                        {role.label}
                        {IconAddMS}
                      </ChipOption>
                    );
                  })}
                </OptionsBox>
              )}
            </MultiSelectContainer>
          )}
          <Box display="flex" gap="20px" justifyContent="flex-end">
            <OutlinedButton onClick={handleClose}>
              {Constants.cancel}
            </OutlinedButton>
            <Button
              form="edituser"
              type="submit"
              disabled={emailError || nameError}
              sx={{ width: '200px' }}
            >
              {Constants.save}
            </Button>
          </Box>
        </FormStyled>
      </DialogContainer>
    </StyledDialog>
  );
}
